/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import Fuse from "fuse.js";
import { Row, Col } from "react-awesome-styled-grid";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { Box, Input, Table, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { fuseOptions, highlightQueryText } from "~/components/Search/common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { RouteParams } from "~/models";
import { useAxios, useClickOutside, useQueryState } from "~/tools";
import { CardHeader, Inner, Result, SearchWrap } from "./FilteredSearch";

type Env = {
  id: string;
  name: string;
};

export type SearchItem = {
  id: string;
  name: string;
  environments: Array<Env>;
};

const Wrap = styled.div`
  transform: translateY(-2px);
`;

type Props = {
  removed: boolean;
};

/* eslint-disable no-nested-ternary */
function ApplicationSearch({ removed }: Props) {
  const { customerId } = useParams<RouteParams>();

  const [isLoading, setIsLoading] = React.useState(false);
  const [searchData, setSearchData] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [showResults, setShowResults] = React.useState(false);

  const wrapperRef = React.useRef(null);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [, , updateQueryState] = useQueryState();

  useClickOutside(wrapperRef, () => setShowResults(false), showResults);

  const opts = { keys: ["name", "environments.name"], ...fuseOptions };
  const fuse = new Fuse(searchData, opts);
  const results = fuse
    .search(query)
    .map(({ item }) => item)
    .slice(0, 50);

  const fetchSearchData = React.useCallback(async () => {
    try {
      const url = `/customers/${customerId}/applications/search`;
      const { data } = await Axios().get(url, { removed });
      data.searchResults && setSearchData(data.searchResults);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, removed, setSearchData, showError, setIsLoading]);

  React.useEffect(() => {
    fetchSearchData();
  }, [fetchSearchData]);

  return (
    <Box shadow="shadow00" innerSpacing="spacing03">
      <Row>
        <Col md="12">
          <FlexContainer grow>
            <SearchWrap ref={wrapperRef}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  updateQueryState({ query, page: "0" });
                  setShowResults(false);
                }}
              >
                <Input
                  loading={isLoading}
                  type="search"
                  value={query}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setQuery(e.target.value);
                    setShowResults(e.target.value.length > 0);
                  }}
                />
              </form>

              {showResults && results?.length > 0 && (
                <Inner key="isSearchBar">
                  <Box shadow="shadow04">
                    <CardHeader>
                      Top results
                      {removed ? " (removed applications)" : null}
                    </CardHeader>
                    <Result>
                      <Table.thead>
                        <Table.tr>
                          <Table.th>Application name</Table.th>
                          <Table.th>Environments</Table.th>
                          <Table.th css={{ width: "35%", textAlign: "right" }}>
                            Application ID
                          </Table.th>
                        </Table.tr>
                      </Table.thead>

                      <Table.tbody>
                        {results?.map((item: SearchItem) => (
                          <Table.tr key={item.id}>
                            <Table.td>
                              {!removed ? (
                                <Link
                                  key={item.id}
                                  to={`applications/${item.id}`}
                                >
                                  {highlightQueryText(item.name, query)}
                                </Link>
                              ) : (
                                <Text key={item.id}>
                                  {highlightQueryText(item.name, query)}
                                </Text>
                              )}
                            </Table.td>

                            <Table.td>
                              <BelongsTo
                                item={item}
                                removed={removed}
                                query={query}
                              />
                            </Table.td>

                            <Table.td
                              css={{ width: "25%", textAlign: "right" }}
                            >
                              <Text size="sm">{item.id}</Text>
                            </Table.td>
                          </Table.tr>
                        ))}
                      </Table.tbody>
                    </Result>
                  </Box>
                </Inner>
              )}
            </SearchWrap>
          </FlexContainer>
        </Col>
      </Row>
    </Box>
  );
}

type BelongsToProps = {
  item: SearchItem;
  removed: boolean;
  query: string;
};

function BelongsTo({ item, removed, query }: BelongsToProps) {
  return (
    <Wrap>
      <Text size="sm" tag="div">
        <FlexContainer direction="column" align="flex-start">
          {item.environments.length === 0 && <>&ndash;</>}

          {item?.environments?.map((env: Env) =>
            !removed ? (
              <Link key={env.id} to={`${item.id}/${env.id}`}>
                {highlightQueryText(env.name, query)}
              </Link>
            ) : (
              <Text size="sm" key={env.id}>
                {highlightQueryText(env.name, query)}
              </Text>
            )
          )}
        </FlexContainer>
      </Text>
    </Wrap>
  );
}

export { ApplicationSearch };
